import React, { useState, useEffect } from "react";

import { Auth } from "aws-amplify";
import OtpInput from "react-otp-input";

import { analyticsService } from "../../services/AnalyticsService";
import Loader from "../../containers/LoadingScreen/LoadingScreen";
import { Container, Title, Resend } from "../GeneralStyles/GeneralStyles";
import { Paragraph, Success, Icon, ResentMessage, SupportMessage, ErrorMessage, Row } from "./Styled";
import Valid from "../../containers/Signup/Assets/Valid.svg";
import Attention from "../../containers/Signup/Assets/Attention.svg";
import { registrationUrl, baseUrl, loginUrl } from "../../utils/env";
import { reportError } from "../../utils/rollbar";
import { obscureEmail, resendEmail } from "../../utils/emailsUtils";
import { useFade } from "../../hooks/useFade";
import { authenticate, checkUserCreationCompletion } from "../../containers/Signup/Signup.service";
import { useSignup } from "../../contexts/SignupContext";
import { determineUserType } from "../../utils/UserType";

const EmailConfirmation = () => {
  const { cognitoUser, UUID, academyId, country_code, password, url } = useSignup();
  const userType = determineUserType();
  const [code, setCode] = useState(null);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [codeResent, setCodeResent] = useState(false);
  const [resetInput, setResetInput] = useState(false);
  const [isVisible, setIsVisible, fadeProps] = useFade(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (error) {
      setCode(null);
    }
  }, [error]);

  // Since in Cognito the both SMS and email are used for verification, we use Auth.verifyUserAttribute to verify email as well as SMS
  const handleCandidateSignup = async (code: any) => {
    try {
      await Auth.verifyUserAttributeSubmit(cognitoUser, "email", code);
      setSuccess(true);
      analyticsService.trackSuccess(cognitoUser.username, UUID, academyId, country_code);
      window.location.href = `${baseUrl}/academy/?token=${UUID}`;
    } catch (error) {
      handleSignupError(error);
    }
  };

  // Since the self service signup is using Email  as default verification method, we can just use Auth.confirmSignUp
  const handleSelfServiceSignup = async (code: any) => {
    try {
      const startTime = new Date();
      analyticsService.track("SES - Confirmation Code Submitted", {}, () => {});

      await Auth.confirmSignUp(cognitoUser.username, code);

      if (cognitoUser.userDataKey) {
        await checkUserCreationCompletion(setLoading, setError, password, cognitoUser, cognitoUser.username);
        await authenticate(password, cognitoUser, cognitoUser.username, userType, startTime, url);
      } else {
        setTimeout(() => {
          if (loginUrl) {
            window.location.href = loginUrl;
          } else {
            console.error("loginUrl is not defined.");
          }
        }, 5000);
      }
    } catch (err) {
      handleSignupError(err, true); // 'true' indicates resetInput should be set
    }
  };

  const handleSignupError = (error: any, shouldResetInput = false) => {
    console.log("error confirming sign up", error);
    setError(true);
    setLoading(false);
    setResetInput(shouldResetInput);
    setErrorMessage(error.message);
    const reportLevel = error.code === "CodeMismatchException" ? "info" : "error";
    analyticsService.trackError(error, cognitoUser.username);
    reportError("Failed to get confirm signup", error, reportLevel);
  };

  const handleChange = async (code: any) => {
    setResetInput(false);
    setCode(code);
    setLoading(true);

    const isCandidateSignup = code?.length === 6 && userType === "candidate";
    const isSelfServiceSignup = code?.length === 6 && userType === "selfService";

    if (isCandidateSignup) {
      await handleCandidateSignup(code);
    } else if (isSelfServiceSignup) {
      await handleSelfServiceSignup(code);
    }

    setLoading(false);
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Container {...fadeProps}>
          <Title>Verify your email address</Title>
          <Paragraph>
            We have sent a code to: {obscureEmail(cognitoUser.username, setError)}.
            <br />
            Enter the code below to confirm your email address.
            {codeResent ? (
              <ResentMessage>
                <Icon src={Valid} alt="checkmark" />
                <Resend Color="#008A24">A new code has been sent to you</Resend>
              </ResentMessage>
            ) : (
              <Resend
                Color="#757584"
                Decoration="underline"
                onClick={(e) => {
                  e.preventDefault();
                  resendEmail(userType, cognitoUser, setCodeResent, analyticsService, reportError, setError, setErrorMessage).catch((err) => {
                    console.error("Error in resendEmail", err);
                  });
                }}>
                Resend email
              </Resend>
            )}
          </Paragraph>
          <div className="otp-wrapper" style={{ display: "block" }}>
            <OtpInput
              numInputs={6}
              value={resetInput || code === null ? undefined : code}
              onChange={handleChange}
              inputStyle="otp-code__input"
              shouldAutoFocus={true}
              isInputNum={true}
              hasErrored={error}
              errorStyle="otp-code__input--error"
              isDisabled={loading}
            />
            {success && <Success>User created and confirmed!</Success>}
            {error &&
              errorMessage &&
              ((errorMessage as any).includes("Invalid verification code provided, please try again.") ? (
                <Row>
                  <Icon src={Attention} alt="error icon"></Icon>
                  <ErrorMessage>Incorrect code. Please try again.</ErrorMessage>
                </Row>
              ) : userType === "selfService" ? (
                <Row>
                  <Icon src={Attention} alt="error icon"></Icon>
                  <ErrorMessage>
                    Sign-up error. Please <a href={registrationUrl}>retry</a> or{" "}
                    <a href="https://support.verbit.ai/hc/en-us/articles/360015514940-How-to-Contact-Verbit-Customer-Support" target="_blank" rel="noreferrer">
                      contact support.
                    </a>
                  </ErrorMessage>
                </Row>
              ) : (
                <Row>
                  <Icon src={Attention} alt="error icon"></Icon>
                  <ErrorMessage>Something went wrong!</ErrorMessage>
                </Row>
              ))}
            <SupportMessage>
              If you haven’t received the email, check your spam account. If it is not there, try to register again or contact{" "}
              <a href="https://support.verbit.ai/hc/en-us/articles/360015514940-How-to-Contact-Verbit-Customer-Support" target="_blank" rel="noreferrer">
                Customer Support
              </a>
              .
            </SupportMessage>
          </div>
        </Container>
      )}
    </>
  );
};

export default EmailConfirmation;
